import {
  type GetResolutionOptionsResult,
  type ResolutionOption,
} from '../graphql/query/getResolutionOptions';
import { QUERIES } from '../utils/constants';
import { useWorkflow } from '../workflow/hooks';
import { generateQueryKey } from '../utils/queries';
import { type WorkflowData } from '../workflow';
import { queryClient } from '../queryClient';
import { useMemo } from 'react';
import type { OrderItem } from '../graphql/query/getOrder';
import { useItems } from './useItems';
import type { ResolutionData } from '../components/tasks/ResolutionTask/ResolutionTaskSchema';
import type { ReasonItemData } from '../components/tasks/ReasonsTask/ReasonsTaskSchema';

// Typeguard
const isReasonAndResolutionData = (
  data: unknown,
): data is {
  reason: ReasonItemData;
  resolution: {
    resolutionId?: string;
    exchangeItem?: { productId: string; variantId: string };
  };
} => {
  if (data === null || typeof data !== 'object') {
    return false;
  }

  const obj = data as Record<string, unknown>;

  // Check if reason exists and has the expected structure
  if (typeof obj.reason !== 'object' || obj.reason === null) {
    return false;
  }

  const reason = obj.reason as Record<string, unknown>;
  const hasValidReason =
    reason.reasonId !== undefined || reason.nestedReasonId !== undefined;

  if (!hasValidReason) {
    return false;
  }

  // Check if resolution exists and has the expected structure
  if (typeof obj.resolution !== 'object' || obj.resolution === null) {
    return false;
  }

  const resolution = obj.resolution as Record<string, unknown>;
  const hasValidResolution =
    resolution.resolutionId !== undefined ||
    resolution.exchangeItem !== undefined;

  return hasValidResolution;
};

export type ResolutionByItem = {
  selected: ResolutionData['resolution'];
  options: ResolutionOption[];
};

export type UseResolutionsArgs = {
  itemId?: string;
};

export const useResolutions = () => {
  const { config, data, isLoading } = useWorkflow();
  const items = useItems();
  let workflowItems: WorkflowData['items'];

  if (!data?.items) {
    // For flows where there is no item selection
    if (isReasonAndResolutionData(data)) {
      workflowItems = items.reduce<NonNullable<WorkflowData['items']>>(
        (acc, c) => {
          acc[c.id] = {
            reason: { reasonId: data?.reason?.reasonId },
            resolution: { resolutionId: data?.resolution?.resolutionId },
          };
          return acc;
        },
        {} as NonNullable<WorkflowData['items']>,
      );
    }
  } else {
    workflowItems = data?.items;
  }

  if (!workflowItems) {
    // If still loading, return empty object instead of throwing
    if (isLoading) {
      return {};
    }
    throw new Error('Cannot resolve without item data');
  }

  const resolutionOptionsByItem = useMemo(() => {
    return Object.keys(workflowItems).reduce<
      Record<OrderItem['id'], ResolutionByItem>
    >((acc, itemId) => {
      const queryKey = generateQueryKey(QUERIES.GET_RESOLUTION_OPTIONS, {
        currentItemId: itemId,
        taskIds: ['reason'],
        workflowConfig: config,
        workflowData: data,
      });
      const qData =
        queryClient.getQueryData<GetResolutionOptionsResult>(queryKey);

      const selected = workflowItems[itemId]?.resolution;
      const options = qData?.resolutionOptions || [];
      if (selected && options) {
        return {
          ...acc,
          [itemId]: {
            selected,
            options,
          },
        };
      }
      return acc;
    }, {});
  }, [data, workflowItems, config]);

  return resolutionOptionsByItem;
};
